import { formatNameLastFirst } from '@strata/helper-functions';
import { Hyperlink } from '@strata/react-hyperlink';
import PropTypes from 'prop-types';

import { useGet } from 'services/Http';
import { ListSchema, ListSchemaAppRoutePrefix, ListType } from 'services/ListManagementHelpers';
import {
    ConditionType,
    ConditionTypeNameMap,
    getSelectedProductType,
} from 'services/RouteRuleHelpers';

const PrescriberConditionValue = ({ npi }) => {
    const prescriber = useGet(npi ? `/reference/prescribers/${npi}` : null);
    return prescriber?.first_name
        ? formatNameLastFirst(prescriber.last_name, prescriber.first_name)
        : npi;
};

const getProductName = (isBrand, product) => {
    if (!product) {
        return null;
    }
    return isBrand ? product.brandProductDescription : product.genericProductDescription;
};

const getListTypeForCondition = (condition) =>
    ({
        [ConditionType.ACCOUNT_LIST]: ListType.account,
        [ConditionType.GROUP_LIST]: ListType.group,
        [ConditionType.NETWORK_LIST]: ListType.network,
        [ConditionType.PRESCRIBER_LIST]: ListType.prescriber,
        [ConditionType.PRODUCT_LIST]: ListType.product,
        [ConditionType.THERAPEUTIC_CATEGORY_LIST]: ListType.therapeutic_category,
        [ConditionType.ZIP_CODE_LIST]: ListType.zip_code,
    })[condition.condition_type];

export const RouteConditionValue = ({ condition = undefined, conditionOptionsMap }) => {
    switch (condition.condition_type) {
        case ConditionType.PLACE_OF_SERVICE:
            return (conditionOptionsMap[condition.condition_type] ?? [])
                .filter(({ value }) => condition.value.includes(value))
                .map(({ name }) => name)
                .join(', ');
        case ConditionType.PRESCRIBER:
            return <PrescriberConditionValue npi={condition.value} />;
        case ConditionType.PRODUCT: {
            const product = conditionOptionsMap[condition.condition_type]?.find(
                ({ value }) => value === condition.value?.product_service_oid,
            );
            return getProductName(condition.value?.is_brand, product);
        }
        case ConditionType.PRODUCT_TYPE:
            return getSelectedProductType(condition.value);
        case ConditionType.SINGLE_SERVICING_SUPPLIER:
            return ConditionTypeNameMap[ConditionType.SINGLE_SERVICING_SUPPLIER];
        case ConditionType.DIAGNOSIS:
        case ConditionType.SUPPLIER:
            return conditionOptionsMap[condition.condition_type]?.find(
                ({ value }) => value === condition.value,
            )?.name;
        /** Multi-list conditions */
        case ConditionType.PRODUCT_LIST: {
            const listType = getListTypeForCondition(condition);
            return (
                <span className="flex__gap--x-small" style={{ flexDirection: 'column' }}>
                    {condition.value.map((val) => (
                        <Hyperlink
                            key={val}
                            linkText={
                                conditionOptionsMap[condition.condition_type]?.find(
                                    ({ value }) => value === val,
                                )?.name
                            }
                            navUrl={`${ListSchemaAppRoutePrefix[ListSchema.assignment]}/${listType}/${val}`}
                        />
                    ))}
                </span>
            );
        }
        /** Single-list conditions */
        case ConditionType.ACCOUNT_LIST:
        case ConditionType.GROUP_LIST:
        case ConditionType.NETWORK_LIST:
        case ConditionType.PRESCRIBER_LIST:
        case ConditionType.THERAPEUTIC_CATEGORY_LIST:
        case ConditionType.ZIP_CODE_LIST: {
            const listType = getListTypeForCondition(condition);
            return (
                <Hyperlink
                    linkText={
                        conditionOptionsMap[condition.condition_type]?.find(
                            ({ value }) => value === condition.value,
                        )?.name
                    }
                    navUrl={`${ListSchemaAppRoutePrefix[ListSchema.assignment]}/${listType}/${condition.value}`}
                />
            );
        }
        default:
            return null;
    }
};

RouteConditionValue.propTypes = {
    condition: PropTypes.shape({
        condition_type: PropTypes.oneOf(Object.values(ConditionType)),
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({}),
            PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
        ]),
    }),
    conditionOptionsMap: PropTypes.shape({}).isRequired,
};
